import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Linkedin, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila i campi obbligatori (nome, email, messaggio).");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Messaggio inviato. Ti risponderò personalmente al più presto.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Si è verificato un errore. Riprova tra qualche minuto.";
      toast.error(typeof detail === "string" ? detail : "Errore nell'invio del modulo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contatti"
      data-testid="contact-section"
      className="py-24 lg:py-32 border-t border-stone-200 bg-[#FAFAF9]"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: copy & contacts */}
          <div className="lg:col-span-5">
            <span className="eyebrow">Contatti</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-stone-900 leading-[1.1]">
              Scrivimi: ricevo personalmente ogni messaggio.
            </h2>
            <p className="mt-6 text-stone-600 leading-relaxed">
              Per richieste informative o per individuare la sede più adatta al tuo
              esame, compila il modulo o utilizza i canali sotto. Per le prenotazioni standard ti
              invito a passare dai portali ufficiali nella sezione <a href="#sedi" className="underline underline-offset-4 hover:text-[#3E615B]">Sedi</a>.
            </p>

            <div className="mt-10 space-y-5">
              <a
                data-testid="contact-email"
                href="mailto:silviadalbello@casadicurapalazzolo.it"
                className="flex items-center gap-4 group"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white border border-stone-200 text-[#3E615B] group-hover:bg-[#3E615B] group-hover:text-white transition-colors">
                  <Mail size={18} />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-stone-500">Email</p>
                  <p className="text-stone-900 font-medium">
                    silviadalbello@casadicurapalazzolo.it
                  </p>
                </div>
              </a>

              <a
                data-testid="contact-linkedin"
                href="https://www.linkedin.com/in/silvia-dal-bello-b2ab90183/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white border border-stone-200 text-[#3E615B] group-hover:bg-[#3E615B] group-hover:text-white transition-colors">
                  <Linkedin size={18} />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-stone-500">
                    Profilo professionale
                  </p>
                  <p className="text-stone-900 font-medium">LinkedIn</p>
                </div>
              </a>
            </div>

            <div className="mt-10 rounded-2xl border border-stone-200 bg-white p-6 text-sm text-stone-600 leading-relaxed">
              <p className="text-[11px] uppercase tracking-[0.22em] text-stone-500 mb-2">
                Nota importante
              </p>
              Questo modulo non sostituisce un consulto medico né può essere utilizzato per emergenze
              cliniche. In caso di urgenza contatta il Pronto Soccorso o il 112.
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="rounded-3xl border border-stone-200 bg-white p-7 lg:p-10 shadow-sm"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-stone-700">
                    Nome e cognome <span className="text-red-700">*</span>
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Mario Rossi"
                    required
                    className="mt-2 h-11 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-stone-700">
                    Email <span className="text-red-700">*</span>
                  </Label>
                  <Input
                    id="email"
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="nome@email.it"
                    required
                    className="mt-2 h-11 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-stone-700">
                    Telefono
                  </Label>
                  <Input
                    id="phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+39 …"
                    className="mt-2 h-11 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="subject" className="text-stone-700">
                    Oggetto
                  </Label>
                  <Input
                    id="subject"
                    data-testid="contact-input-subject"
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="Es. Informazioni RM Addome"
                    className="mt-2 h-11 rounded-xl"
                  />
                </div>
              </div>

              <div className="mt-5">
                <Label htmlFor="message" className="text-stone-700">
                  Messaggio <span className="text-red-700">*</span>
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Descrivi brevemente la tua richiesta…"
                  required
                  rows={6}
                  className="mt-2 rounded-xl resize-none"
                />
              </div>

              <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-xs text-stone-500 max-w-md">
                  Inviando il modulo accetti il trattamento dei dati ai fini della risposta alla tua
                  richiesta. Nessun dato sanitario sarà richiesto in questa fase.
                </p>
                <Button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="rounded-full bg-[#3E615B] hover:bg-[#2D4742] text-white px-7 py-6 text-sm inline-flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Invio…
                    </>
                  ) : (
                    <>
                      Invia messaggio
                      <Send size={15} />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
